import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import {
  ESeverityColors,
  IPolicy,
  SEVERITY_LIST,
} from '../../types/policies-types';
import useAccordion from '../../hooks/useAccordion';
import StyledAccordion, {
  StyledAccordionSummary,
  StyledAccordionDetails,
  StyledAccordionActions,
} from '../CatergoriesAccordion/CatergoriesAccordion.styled';
import { useSnapshot } from 'valtio';
import PoliciesStore from '../../store/policies';
import SeverityDropDown from '../../components/SeverityDropDown/SeverityDropDown';
import PolicySwitchButton from '../../components/PolicySwitchButton/PolicySwitchButton';
import DynamicArgs from '../../components/DynamicArgs/DynamicArgs';
import { useSearchParams } from 'react-router-dom';
import { debounce, isNumber } from 'lodash';
import SeverityToolDropDown from '../../components/IssueSeverityDropDown/IssueSeverityDropDown';
import InfoAdornment from '../../components/InputAdornment/InputAdornment';

interface IAccordionPolicyItemProps {
  policy: IPolicy;
}

const AccordionPolicyItem: React.FC<
  React.PropsWithChildren<IAccordionPolicyItemProps>
> = ({ policy }) => {
  const policyAccordionRef = useRef(null);
  const [expanded, handleChange, setExpanded] = useAccordion();
  const { dirtyPolicies } = useSnapshot(PoliciesStore);
  const { id, selected, severity, displayIssueSeverity } = policy;
  const [searchParams] = useSearchParams();
  const policyId = searchParams.get('policyId');

  const dirtyPolicy = useMemo(
    () => ({
      id,
      selected,
      severity,
      displayIssueSeverity,
      ...dirtyPolicies?.[id],
    }),
    [dirtyPolicies, displayIssueSeverity, id, selected, severity],
  );

  useEffect(() => {
    if (policyAccordionRef && policyAccordionRef.current) {
      if (
        (policyAccordionRef.current as HTMLElement).dataset['policyId'] ===
        policyId
      ) {
        const elem = policyAccordionRef.current as HTMLElement;
        debounce(() => {
          setExpanded(policyId);
          elem.scrollIntoView({
            behavior: 'smooth',
            inline: 'nearest',
          });
        }, 600)();
      }
    }
  }, [policyId, setExpanded]);

  const severityColor = useMemo(() => {
    const severityColorEnumElement = SEVERITY_LIST.find(([key, value]) => {
      return value === dirtyPolicy.severity;
    });
    return severityColorEnumElement && severityColorEnumElement[0];
  }, [dirtyPolicy.severity]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        paddingInline: 30,
      }}
      data-policy-id={policy.policyId}
      ref={policyAccordionRef}>
      <Box
        component='span'
        sx={theme => ({
          backgroundColor: severityColor
            ? ESeverityColors[severityColor]
            : undefined,
          width: theme.spacing(0.6),
          marginBlock: theme.spacing(0.2),
          borderRadius: theme.shape.borderRadius,
        })}></Box>
      <StyledAccordion
        expanded={expanded === policy.policyId}
        onChange={handleChange(
          policy.policyId,
          `?categoryId=${policy.catId}&policyId=${policy.policyId}`,
        )}>
        <StyledAccordionSummary>
          <Stack direction='row' flexGrow={1} gap={2} alignItems='center'>
            <Stack direction='row' flexGrow={1}>
              <Tooltip arrow placement='top-start' title={policy.description}>
                <Typography variant='body2' color='text.primary'>
                  {policy.name}
                </Typography>
              </Tooltip>
            </Stack>
            <Box gap={1} display='inline-flex' flexDirection='row'>
              <InfoAdornment tooltip='This selects the baseline severity that an issue generated from the policy has. 
              The actual severity is calculated based on additional factors starting from the baseline.' />
              <SeverityDropDown
                id={policy.id}
                severity={dirtyPolicy.severity}
              />
            </Box>
            <PolicySwitchButton
              id={policy.id}
              isPolicySelected={dirtyPolicy.selected}
            />
          </Stack>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <Typography p={1} color='text.primary' variant='body2'>
            {policy.description}
          </Typography>
          <Typography
            p={1}
            component='p'
            color='text.secondary'
            variant='caption'>
            {policy.detailedDescription}
          </Typography>
        </StyledAccordionDetails>
        <StyledAccordionActions>
          {isNumber(dirtyPolicy.displayIssueSeverity) && (
            <Box
              p={1}
              ml={1}
              mb={2}
              display='inline-flex'
              gap={1}
              flexDirection='row'>
              <InfoAdornment tooltip='The selection determines which issues are shown for this policy in the Issue screen.' />
              <SeverityToolDropDown
                id={policy.id}
                severity={dirtyPolicy.displayIssueSeverity}
              />
            </Box>
          )}
          <DynamicArgs policyId={policy.id} args={policy.args} />
          {policy.notes && (
            <Stack flexDirection='column' pl={1} mb={2}>
              {policy.notes.map(note => {
                return (
                  <Typography
                    fontStyle='italic'
                    color='text.secondary'
                    variant='body2'>
                    *{note}
                  </Typography>
                );
              })}
            </Stack>
          )}
        </StyledAccordionActions>
      </StyledAccordion>
    </div>
  );
};

export default AccordionPolicyItem;
